// The browser side of the timer (Mi espacio), without React, so it can be tested
// with a simulated clock and a simulated network (scripts/test-owner-time.mjs).
// The server (netlify/functions/shared/ownerTimer.ts) decides everything that is
// data; this only queues Ale's actions with the moment she acted, shows them at
// once and decides when the discreet notice appears.

/**
 * The notice of a running stretch (pure). It depends only on how long the stretch
 * has run: never on the tab being hidden or on work outside Oike. It shows once per
 * `noticeMinutes` step until Ale says "Sigo" (ackStep), and it never stops anything.
 */
export function noticeState({ startedAt, now, noticeMinutes = 90, reviewMinutes = 240, ackStep = 0 }) {
  const start = Date.parse(startedAt || '');
  if (!Number.isFinite(start)) return { elapsedMs: 0, elapsedMinutes: 0, step: 0, show: false, overReview: false };
  const elapsedMs = Math.max(0, now - start);
  const elapsedMinutes = elapsedMs / 60000;
  const step = Math.floor(elapsedMinutes / noticeMinutes);
  return { elapsedMs, elapsedMinutes, step, show: step >= 1 && ackStep < step, overReview: elapsedMinutes > reviewMinutes };
}

/** What a stop sends: the stretch id, or for a stretch started offline (no id yet), its start. */
export function stopBody(run, reason) {
  return run.id && run.id !== 'pending' ? { action: 'timerStop', id: run.id, reason } : { action: 'timerStop', startedAt: run.startedAt, reason };
}

/** The state shown at once after an action, until the server's next read confirms it. */
export function optimistic(state, op) {
  const st = state || {};
  if (op.action === 'timerStart') return { ...st, running: { id: 'pending', startedAt: op.at, taskId: op.taskId || null, taskTitle: op.title || null, note: op.note || '' }, paused: null };
  if (op.action === 'timerStop' && st.running) return { ...st, running: null, paused: op.reason === 'pause' ? { ...st.running, endedAt: op.at } : null };
  return st;
}

/**
 * The queue of actions not yet confirmed by the server, kept in this browser
 * (`storage`: localStorage, shared by the tabs of the same browser). Each action
 * carries the moment Ale acted (`at`), so sending it later does not add time.
 * - The actions are sent one at a time and IN ORDER, by one tab at a time. Two tabs
 *   sending at once could deliver a later start before an earlier one, and the server
 *   (rightly) does not apply a start older than the timer's last change: a stretch
 *   that really happened would be lost. So flush() first takes the queue's lock:
 *   the browser's own lock (`navigator.locks`, the other tab waits and then finds
 *   what is left), or, without it, a lease in `storage` (the other tab answers
 *   `busy` and the holder sends what the other tab queued; a lease left by a closed
 *   tab expires after `leaseMs`).
 * - Without connection the action goes back to the front and the rest wait, in order.
 * - The server never duplicates a retried action and never lets an old one
 *   override a later decision: it answers `stale` (not applied) or `conflict`
 *   (the stretch goes to "por confirmar"). Those come back so the screen says so.
 */
export function createOutbox({ storage, call, key = 'oike_timer_outbox', locks = globalThis.navigator?.locks, now = () => Date.now(), leaseMs = 20000, owner = `${Math.random().toString(36).slice(2)}-${Date.now()}` }) {
  const read = () => { try { const v = JSON.parse(storage.getItem(key) || '[]'); return Array.isArray(v) ? v : []; } catch { return []; } };
  const write = (list) => { try { storage.setItem(key, JSON.stringify(list)); } catch { /* per-browser only */ } };
  const lockKey = `${key}:lock`;
  const lease = () => { try { return JSON.parse(storage.getItem(lockKey) || 'null'); } catch { return null; } };
  const setLease = (v) => { try { storage.setItem(lockKey, JSON.stringify(v)); } catch { /* per-browser only */ } };
  const drain = async (renew = () => {}) => {
    const out = { sent: 0, stale: [], conflicts: [], errors: [], offline: false, busy: false };
    for (;;) {
      const queue = read();
      if (!queue.length) break;
      const [op, ...rest] = queue;
      write(rest);
      renew();
      try {
        const r = await call(op);
        out.sent++;
        if (r?.stale) out.stale.push(op);
        if (r?.conflict) out.conflicts.push(op);
      } catch (e) {
        if (!e?.status) { write([op, ...read()]); out.offline = true; break; } // no connection: back to the front, in order
        if (e.status !== 404) out.errors.push(e.message || String(e.status)); // 404: that stretch no longer exists
      }
    }
    out.remaining = read().length;
    return out;
  };
  return {
    pending: read,
    enqueue(op) { write([...read(), op]); return op; },
    async flush() {
      // The browser's lock: exclusive across tabs; the second tab waits its turn (then the queue holds only what is left)
      if (locks?.request) return locks.request(lockKey, () => drain());
      // A lease in storage: taken and checked in the same synchronous step
      const held = lease();
      if (held && held.owner !== owner && held.until > now()) return { sent: 0, stale: [], conflicts: [], errors: [], offline: false, busy: true, remaining: read().length };
      setLease({ owner, until: now() + leaseMs });
      if (lease()?.owner !== owner) return { sent: 0, stale: [], conflicts: [], errors: [], offline: false, busy: true, remaining: read().length };
      try { return await drain(() => setLease({ owner, until: now() + leaseMs })); }
      finally { if (lease()?.owner === owner) setLease(null); }
    },
  };
}
